

import { Component, Input, OnInit } from '@angular/core';
import { PtlNotificationService, PtlNotificationStyle, PtlWindowLocationService } from '@ptl/global-components';
import { PtlSessionService } from './services/ptl-session.service';
import { REFERRAL_QUERY_PARAM_KEYS, STATELESS_SESSION_PARAM_KEYS } from '../../../../shared/enums/query-params.enum';
import { PtlSessionIndividualObject, PtlSessionObject } from './interfaces/ptl-session.interface';
import { PRODUCT_GROUPS } from 'src/app/angular-elements/plan-selection/enums/product-groups.enum';
import { CommonUtilityService } from 'src/app/shared/utility-services/common-utility.service';
import { LINE_OF_BUSINESS_ENUM } from '@ptl/account-management';
import { MomentTimeZoneEnum } from 'src/app/shared/enums/moment-timezone.enum';
import { ENROLLMENT_FLOW } from 'src/app/angular-elements/form-wrappers/plan-enrollment/individual-plan-enrollment-form/configs/interfaces/enrollment-flow.enum';
import { environment } from 'src/environments/environment';
import moment, { Moment } from 'moment';
import { DeleteAppService } from 'src/app/angular-elements/producer/saved-quotes/services/delete-app.service';
import { SEP_FAMILY_HOUSEHOLD_CHANGE_EVENTS } from 'src/app/angular-elements/producer/saved-quotes/interfaces/save-quote-data.interface';

@Component({
    selector: 'app-ptl-session-loader',
    templateUrl: './ptl-session-loader.component.html',
    styleUrls: ['./ptl-session-loader.component.scss'],
    standalone: false
})
export class PtlSessionLoaderComponent implements OnInit {

  @Input() loadingMsg: string = 'Please wait while we load your information';
  @Input() planDetailsUrl: string = 'view-plans/plan-details'
  @Input() enrollUrl: string = '/enroll/begin-application'

  @Input() marketType: LINE_OF_BUSINESS_ENUM;

  // Specific to Individual Markets at this time
  @Input() janEffExpiryDate: string;
  @Input() oepEndExpiryDate: string;

  @Input() expiredQuoteUrl: string = '/expired-quote';

  @Input() errorTitle: string = 'Error Loading Information';
  @Input() errorMsg: string = 'We couldn\'t load your specific plan information. You will now be taken to our full list of plans.';
  @Input() errorUrl: string = 'view-plans';

  url: URL = new URL(window.location.href);
  params: URLSearchParams = this.url.searchParams;

  constructor(
    private windowService: PtlWindowLocationService,
    private ptlSessionService: PtlSessionService,
    private ptlNotificationService: PtlNotificationService,
    private commonUtilityService: CommonUtilityService,
    private deleteQuoteService: DeleteAppService
  ) { }

  async ngOnInit() {
    let referralId = this.params.get(REFERRAL_QUERY_PARAM_KEYS.REFERRAL_ID_PARAM);
    const marketTypeStandardized = this.marketType.toUpperCase()

    if (referralId) {
      if (marketTypeStandardized === LINE_OF_BUSINESS_ENUM.SENIOR) {
        this.medicareSessionRetrieval(referralId);
      }
      else if (marketTypeStandardized === LINE_OF_BUSINESS_ENUM.INDIVIDUAL) {
        this.individualSessionRetrieval(referralId);
      }
    }
    else {
      this.loadPlanSession();
    }
  }

  async medicareSessionRetrieval(referralId: string) {
    try {
      let sessionObj = await this.ptlSessionService.loadSeniorSessionByReferralId(referralId);

      if (sessionObj?.planSessionResponse) {
        let success = await this.ptlSessionService.applySessionFromState(sessionObj.planSessionResponse);
        if (success) {
          this.windowService.goToUrl(this.planDetailsUrl);
          return;
        }
      }
    }
    catch(e) {
      console.error('error occurred loading senior session', e);
    }
  
    this.showError();
  }

  async individualSessionRetrieval(referralId: string) {
    try {
      let sessionObj = await this.ptlSessionService.loadIndividualSessionByReferralId(referralId);
      
      if (sessionObj?.planSessionResponse) {

        if(this.isExpiredQuote(sessionObj.planSessionResponse)){
          this.deleteQuote(sessionObj?.planSessionResponse?.enrollmentState.appId);
          return;
        }

        await this.ptlSessionService.applyIndividualSessionFromState(sessionObj.planSessionResponse, referralId);
        
        const redirectLocation = sessionObj.planSessionResponse.redirectKey; // this.getRedirectKeyFromSessionObj(sessionObj);
        
        if (redirectLocation) {
          this.windowService.goToUrl(redirectLocation);
        }

        return;
      }
    }
    catch(e) {
      console.error('error occurred loading individual session', e);
    }
  
    this.windowService.goToUrl(this.expiredQuoteUrl);
    return;
  }

  // Users have the ability to land on this page with the following 5 query parameters, independent of any actual referral session
  // The plan data will be loaded accordingly and the user redirected to the desired page
  async loadPlanSession() {
    let planId = this.params.get(STATELESS_SESSION_PARAM_KEYS.PLAN_ID);
    let zipCode = this.params.get(STATELESS_SESSION_PARAM_KEYS.ZIP_CODE);
    let county = this.params.get(STATELESS_SESSION_PARAM_KEYS.COUNTY);
    let planYear = this.params.get(STATELESS_SESSION_PARAM_KEYS.PLAN_YEAR);
    let redirectUrl = this.params.get(STATELESS_SESSION_PARAM_KEYS.REDIRECT_URL) || this.planDetailsUrl;  // default redirect is plan details

    // validate query params
    if (!zipCode || !county || !planId || !planYear) {
      console.error("Invalid query parameters supplied. zipCode, county, planId, and planYear are required");
      return;
    }

    // Validate and apply zip code and county
    let zipCountyResponse = await this.commonUtilityService.setBrandByZipCode(zipCode, county);
    if (zipCountyResponse) {
      // Build a bare minimum session object with the provided info to try and load the plan data
      let success = await this.ptlSessionService.applyPlanFromState({
        planIds: [planId],
        productGroup: PRODUCT_GROUPS.MEDICARE_ADVANTAGE,
        effectiveDateState: {
          effectiveDate: planYear + '-01-01',
          effectiveDateYear: planYear
        }
      } as PtlSessionObject);

      // If everything worked, redirect the user
      if (success) {
        this.windowService.goToUrl(redirectUrl);
        return;
      }
    }

    // If anything fails, show the generic error
    this.showError();
  }

  showError() {
    this.ptlNotificationService.open({
      title: this.errorTitle,
      content: this.errorMsg,
      primaryButtonText: 'OK',
      notificationStyle: PtlNotificationStyle.DANGER,
    })
    .afterClosed().subscribe(() => this.windowService.goToUrl(this.errorUrl));
  }

  isExpiredQuote(planSessionResponse: PtlSessionIndividualObject) {
    
    const enrollmentFlow = (() => {
      const oldPattern = (planSessionResponse.individualMarketState as unknown as { enrollmentFlow: ENROLLMENT_FLOW }).enrollmentFlow; // This goes away once 685331 is deployed 2026-04
      const newPattern = planSessionResponse.saveQuoteData.enrollmentFlow;
      return oldPattern || newPattern;
    })();
    
    const timezone = MomentTimeZoneEnum[environment.configs.siteTimeZone];
    const today = moment().tz(timezone);
    const effectiveDateAsMoment = moment(planSessionResponse?.individualMarketState?.ratingInfo?.effectiveDate).tz(timezone);
  
    /**
     * ENROLLMENT_FLOW values come from Individual Market Save Quote flows
     * Medicare would drop into the else flow
     */
    if (enrollmentFlow === ENROLLMENT_FLOW.OEP) {
      const januaryEffectiveDate = moment().month(0).date(1).format('MM-DD'); //we change the expiry date based on the effective date of the oep quote
      const isJanuaryEffectiveDate = effectiveDateAsMoment.format('MM-DD') === januaryEffectiveDate;
      
      let expiryDateAsMoment: Moment;
      
      if (isJanuaryEffectiveDate) {
        if (!this.janEffExpiryDate) { console.error("Missing January Effective Expiration Date in AEM Configuration!"); }
        expiryDateAsMoment = moment(this.janEffExpiryDate).tz(timezone);
      } else {
        if (!this.oepEndExpiryDate) { console.error("Missing OEP End Expiration Date in AEM Configuration!"); }
        expiryDateAsMoment = moment(this.oepEndExpiryDate).tz(timezone);
      }
      
      if (expiryDateAsMoment.isSameOrBefore(today)) {
        return true;
      } 

    } else if(enrollmentFlow === ENROLLMENT_FLOW.SEP_ELECTRONIC || enrollmentFlow === ENROLLMENT_FLOW.SEP_PAPER){
      const sepLifeEvent = planSessionResponse?.['special-enrollment-circumstance'];
      if (sepLifeEvent?.dateOfLifeEvent && sepLifeEvent?.dateOfLifeEvent !== "") {
        if(SEP_FAMILY_HOUSEHOLD_CHANGE_EVENTS.includes(sepLifeEvent.lifeEvent.lifeEventId)){
          /** For SEP Life events we have two categories, family changes & non-family changes.
           * Family change events like Birth, Adoption, etc. are the only life events that can have a date of life event eff date.
           * Any other life event will have an effective date of 1st of the month of the NEXT month from today's date. 
           * Because family change events can have an effective date in the past, we expire quotes 60 days from the DATE OF LIFE EVENT
           * All other events have an expiration date of the day of the effective date
           */
          //add only 59 days from the event instead of 60 because we count the day of the event 
          const sepExpirationNoTz = moment(sepLifeEvent.dateOfLifeEvent).toISOString().split("T")[0]
          const sepExpirationDateAsMoment = moment.tz(sepExpirationNoTz, timezone).add(59, 'days');
          if(sepExpirationDateAsMoment.isSameOrBefore(today)){
            return true;
          }
        } else {
            if(effectiveDateAsMoment.isSameOrBefore(today)){
              return true;
            }
        }
      }
    }
    return false;
  }

  async deleteQuote(appTrackingId){
    await this.deleteQuoteService.deleteApplication(appTrackingId);
    this.windowService.goToUrl(this.expiredQuoteUrl)
  }
}
